import { z } from "zod";
import { SortOrder } from "../../constants";
import { VALID_FILE_EXTENSIONS } from "../../constants/financial-documents";
import { ActorType } from "../../types/auditLog";
import { AccrualReviewStatus } from "../../types/financialDocument";
import { zQueryBoolean } from "../../types/filters/general";
import { DocumentFlowStatusFiltersSchema } from "./document-flow-status-filters.schema";

export const FinancialDocumentFiltersSchema = z
    .object({
        search: z.string().optional(),
        start: z.coerce.date().optional(),
        end: z.coerce.date().optional(),
        entityIds: z.array(z.coerce.number().int()).optional(),
        hasNoEntity: zQueryBoolean().optional(),
        documentCategoryIds: z.array(z.string().uuid()).optional(),
        financialDocumentIds: z.array(z.coerce.number().int()).optional(),
        importerQueue: z.string().optional(),
        accrualReviewStatuses: z
            .array(z.nativeEnum(AccrualReviewStatus))
            .optional(),
        uploaderIds: z
            .array(z.union([z.literal(null), z.coerce.number().int()]))
            .optional(),
        uploaderType: z.nativeEnum(ActorType).optional(),
        uploaderSources: z.array(z.string()).optional(),
        accountIds: z.array(z.coerce.number().int()).optional(),
        hasNoFinancialAccount: zQueryBoolean().optional(),
        counterpartiesIds: z.array(z.string().uuid()).optional(),
        hasNoCounterparty: z.coerce.boolean().optional(),
        withEmptyOrUnverifiedCounterparty: z.coerce.boolean().optional(),
        periodStart: z.string().date().optional(),
        periodEnd: z.string().date().optional(),
        fileExtensions: z
            .array(z.enum(VALID_FILE_EXTENSIONS as [string, ...string[]]))
            .optional(),
    })
    .merge(DocumentFlowStatusFiltersSchema);

export enum FinancialDocumentSortField {
    CREATED_AT = "createdAt",
    FILE_NAME = "fileName",
}

export const FinancialDocumentSortFieldSchema = z.nativeEnum(
    FinancialDocumentSortField,
);

export const FinancialDocumentSortSchema = z.object({
    field: FinancialDocumentSortFieldSchema,
    order: z.nativeEnum(SortOrder),
});

export const FinancialDocumentQuerySchema = z.object({
    workspaceId: z.string().uuid(),
    sort: FinancialDocumentSortSchema.optional(),
    page: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(0).max(200).default(20),
    filters: FinancialDocumentFiltersSchema.optional(),
    // Strict boolean: the endpoint uses jsonQuery, and z.coerce.boolean()
    // would turn a literal "false" query value into true
    skipCount: z.boolean().optional(),
});

export type FinancialDocumentSort = z.infer<typeof FinancialDocumentSortSchema>;
export type FinancialDocumentQuery = z.infer<
    typeof FinancialDocumentQuerySchema
>;
export type FinancialDocumentFilters = z.infer<
    typeof FinancialDocumentFiltersSchema
>;
